import React, { FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import COUNT from "../../../images/countdown.796030a.png";
import downloadBlob from "../block-countdown/common/downloadBlob";
import createIcsFileBlob from "../block-countdown/common/createIcsFileBlob";
import createGoogleCalendarLink from "../block-countdown/common/createGoogleCalenderLink";
import { blockService } from "../../../store/actions";

interface CountdownData {
  CurrentBlock: string;
  CountdownBlock: string;
  RemainingBlock: string;
  EstimateTimeInSec: string;
}

const BlockCountdown: FC = () => {
  const { block } = useParams();
  const [countdown, setCountdown] = useState<CountdownData | null>(null);
  const [targetTime, setTargetTime] = useState<Date | null>(null);
  const [timeRemaining, setTimeRemaining] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const getBlockCountdown = async () => {
      try {
        setLoading(true);
        const responseData = await blockService.getblockcountdown(block);
        const data = responseData.result;
        setCountdown(data);
        
        // Calculate target time using EstimateTimeInSec
        const estimateSeconds = parseFloat(data.EstimateTimeInSec);
        const targetDate = new Date(Date.now() + estimateSeconds * 1000);
        setTargetTime(targetDate);
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching countdown data:", error);
        setLoading(false);
      }
    };

    if (block) {
      getBlockCountdown();
    }
  }, [block]);

  const handleAddToAppleCalClick = useCallback(() => {
    if (!countdown || !targetTime) return;
    const fileBlob = createIcsFileBlob({
      blockHeight: block!,
      date: dayjs(targetTime),
    });
    downloadBlob(fileBlob, `Block #${block} creation event.ics`);
  }, [countdown, targetTime, block]);

  const handleAddToGoogleCalClick = useCallback(() => {
    if (!countdown || !targetTime) return;
    const googleCalLink = createGoogleCalendarLink({
      blockHeight: block!,
      timeFromNow: Math.floor((targetTime.getTime() - Date.now()) / 1000),
    });
    window.open(googleCalLink, "_blank");
  }, [countdown, targetTime, block]);

  useEffect(() => {
    if (!targetTime) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetTime.getTime() - now;

      if (distance <= 0) {
        setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeRemaining({ days, hours, minutes, seconds });
    };

    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  }, [targetTime]);

  return (
    <section className="container-xxl pt-5 pt-sm-6 pb-12">
      <div className="row gx-0 gy-4">
        <div className="col-xl-5">
          <img className="img-fluid mx-auto" data-img-theme="light" src={COUNT} alt="Block Countdown Image" />
        </div>
        <div className="col-xl-7">
          <h1 className="bs-page-title bs-font">Block Countdown</h1>
          <p className="mb-3">The estimated time for a block to be created and added to the blockchain.</p>

          <div className="row gx-3 gy-4">
            <div className="col-md-6">
              <div className="bs-card shadow-none h-100 p-4">
                <h2 className="fs-base mb-3">
                  <i className="bi bi-stopwatch me-1"></i>Countdown
                </h2>

                <div className="js-countdown d-flex flex-wrap gap-4 countdownPanel">
                  <div className="border-end pe-4">
                    <div className="text-cap">Days</div>
                    <div className="js-cd-days fs-3 text-primary fw-medium lh-1">{timeRemaining.days}</div>
                  </div>

                  <div className="border-end pe-4">
                    <div className="text-cap">Hours</div>
                    <div className="js-cd-hours fs-3 text-primary fw-medium lh-1">{timeRemaining.hours}</div>
                  </div>

                  <div className="border-end pe-4">
                    <div className="text-cap">Mins</div>
                    <div className="js-cd-minutes fs-3 text-primary fw-medium lh-1">{timeRemaining.minutes}</div>
                  </div>

                  <div>
                    <div className="text-cap">Secs</div>
                    <div className="js-cd-seconds fs-3 text-primary fw-medium lh-1">{timeRemaining.seconds}</div>
                  </div>
                </div>

                <hr className="my-4" />

                <h2 className="fs-base mb-3">
                  <i className="bi bi-calendar3 me-1"></i>Estimated Target Date
                </h2>

                <div className="fs-5 text-primary1">
                  <span>{targetTime?.toUTCString() || "Loading..."}</span>
                </div>
                <div className="small text-muted mb-3">
                  <span>UTC</span>
                </div>

                <div className="small fw-medium mb-1">Add to Calendar:</div>
                <div className="d-flex align-items-center gap-2">
                  <a
                    className="btn btn-lg btn-white content-center rounded-pill"
                    style={{ width: "2.375rem", height: "2.375rem" }}
                    onClick={handleAddToGoogleCalClick}
                    href="#"
                  >
                    <i className="bi bi-google"></i>
                  </a>
                  <a
                    className="btn btn-lg btn-white content-center rounded-pill"
                    style={{ width: "2.375rem", height: "2.375rem" }}
                    href="#"
                    onClick={handleAddToAppleCalClick}
                  >
                    <i className="bi bi-apple"></i>
                  </a>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="bs-card shadow-none h-100 p-4">
                <h2 className="fs-base mb-3">
                  <i className="bi bi-box me-1"></i>Block Details
                </h2>

                <div className="d-flex flex-column gap-1 mb-4">
                  <div className="bs-card bg-primary bg-opacity-10 border-primary border-opacity-10 shadow-none p-3 mb-2">
                    <h4 className="h6 text-cap text-dark mb-0.5">Countdown For Block:</h4>
                    <div className="fw-medium text-primary">#{countdown?.CountdownBlock}</div>
                  </div>

                  <div className="bs-card shadow-none p-3 mb-2">
                    <h4 className="h6 text-cap mb-0.5">Remaining Blocks:</h4>
                    <div className="fw-medium">#{countdown?.RemainingBlock}</div>
                  </div>

                  <div className="bs-card shadow-none p-3">
                    <h4 className="h6 text-cap mb-0.5">Current Block:</h4>
                    <div className="fw-medium">#{countdown?.CurrentBlock}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlockCountdown;